import { Controller } from '@hotwired/stimulus'
import { skeletonLoader } from '../utils/skeleton_loader'

export default class extends Controller {
  static targets = ['dropdown', 'dropdownButton']
  static values = {
    clauseIndex: Number
  }

  connect() {
    // Close dropdown when clicking outside
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseOnClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseOnClickOutside)
  }

  // Toggle dropdown (desktop)
  toggleDropdown(event) {
    event.preventDefault()
    event.stopPropagation()

    // Don't toggle if clicking on the remove button
    if (event.target.closest('button[data-action*="removeClause"]')) {
      return
    }

    // Close all other dropdowns first
    this.closeAllDropdowns()

    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.toggle('hidden')

      // Position dropdown
      if (!this.dropdownTarget.classList.contains('hidden')) {
        this.positionDropdown()
      }
    }
  }

  // Open modal (mobile)
  openModal(event) {
    // Modal opening is handled by app--modal-data controller
    // This is just a hook for any additional logic
  }

  // Remove clause
  removeClause(event) {
    event.preventDefault()
    event.stopPropagation()

    // Show skeleton loaders before removing clause (which triggers form submission)
    skeletonLoader.show({ position: 'prepend' })

    // Get clause index from the button or from the controller value
    const button = event.target.closest('button[data-clause-index]')
    const clauseIndex = button ? button.dataset.clauseIndex : this.clauseIndexValue

    // Get the advanced filter controller
    const advancedFilterController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="products--advanced-filter"]'),
      'products--advanced-filter'
    )

    if (advancedFilterController) {
      // Create a fake event with the clause index
      const fakeEvent = {
        target: {
          closest: () => ({
            dataset: {
              clauseIndex: clauseIndex
            }
          })
        }
      }

      advancedFilterController.removeSearchClause(fakeEvent)
    }

    // Close dropdown/modal
    this.closeDropdown()
  }

  // Apply changes and close dropdown/modal
  applyChanges(event) {
    event.preventDefault()
    event.stopPropagation()

    // Get the advanced filter controller
    const advancedFilterController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="products--advanced-filter"]'),
      'products--advanced-filter'
    )

    if (advancedFilterController) {
      // Re-render the pill with updated values
      advancedFilterController.renderSearchClauses()
    }

    // Close dropdown/modal
    this.closeDropdown()
    this.closeModal()
  }

  // Position dropdown relative to pill
  positionDropdown() {
    if (!this.hasDropdownTarget) return

    // Check if keyboard is open (visual viewport is smaller than layout viewport)
    const isKeyboardOpen = window.visualViewport &&
                           window.visualViewport.height < window.innerHeight - 100

    // Don't reposition when keyboard is open to prevent coordinate system mismatch
    if (isKeyboardOpen) return

    const pillRect = this.element.getBoundingClientRect()
    const dropdown = this.dropdownTarget
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Calculate available space below pill
    const spaceBelow = viewportHeight - pillRect.bottom - 16 // 16px padding
    const maxHeight = Math.min(spaceBelow, viewportHeight * 0.7) // Max 70vh or available space

    // Position below the pill
    dropdown.style.position = 'fixed'
    dropdown.style.top = `${pillRect.bottom + 8}px`
    dropdown.style.maxHeight = `${maxHeight}px`

    // On mobile, center the dropdown with padding
    if (viewportWidth < 640) {
      const padding = viewportWidth * 0.025 // 2.5% padding on each side
      dropdown.style.left = `${padding}px`
      dropdown.style.right = `${padding}px`
      dropdown.style.width = 'auto'
      dropdown.style.minWidth = 'auto'
    } else {
      // On desktop, align with pill but ensure it doesn't overflow
      const dropdownWidth = 600
      let leftPosition = pillRect.left
      // If dropdown would overflow right edge, align to right edge with padding
      if (leftPosition + dropdownWidth > viewportWidth - 20) {
        leftPosition = viewportWidth - dropdownWidth - 20
      }

      // Ensure dropdown doesn't overflow left edge
      if (leftPosition < 20) {
        leftPosition = 20
      }

      dropdown.style.left = `${leftPosition}px`
      dropdown.style.right = 'auto'
      dropdown.style.width = 'auto'
      dropdown.style.minWidth = '600px'
    }
  }

  // Close dropdown
  closeDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('hidden')
    }
  }

  // Close modal
  closeModal() {
    const modal = document.getElementById(`filter_edit_modal_${this.clauseIndexValue}`)
    if (modal && modal.open) {
      modal.close()
    }
  }

  // Close all dropdowns
  closeAllDropdowns() {
    document.querySelectorAll('[data-controller*="products--filter-pill"] [data-products--filter-pill-target*="dropdown"]').forEach(dropdown => {
      dropdown.classList.add('hidden')
    })
  }

  // Close dropdown when clicking outside
  closeOnClickOutside(event) {
    // Don't close if form is auto-submitting
    if (window.isAutoSubmitting) {
      return
    }

    // Don't close if clicking inside the pill element
    if (this.element.contains(event.target)) {
      return
    }

    // Don't close if clicking inside the dropdown
    if (this.hasDropdownTarget && this.dropdownTarget.contains(event.target)) {
      return
    }

    // Close if clicking outside both pill and dropdown
    this.closeDropdown()
  }
}
